import { create } from "zustand";
import {
	postTransactionsBundleExecute,
	postTransactionsBundleSimulate,
	type TransactionsBundleExecuteResponse,
	type TransactionsBundleSimulateResponse,
	type WireOp,
} from "./api";

/* --- intent shape ----------------------------------------------------- */

export type CartIntent = WithdrawIntent | RebalanceIntent;

export interface WithdrawIntent {
	id: string;
	kind: "withdraw";
	createdAt: number;
	assetId: string;
	protocol: string;
	vaultAddress: string;
	valueUsdcRaw: string; // signed positive — converted to negative op delta
}

export interface RebalanceIntent {
	id: string;
	kind: "rebalance";
	createdAt: number;
	/** Per-asset signed USDC-raw deltas from the optimizer's plan. */
	deltas: Record<string, string>;
	/** "+5% APY" or similar — display only. */
	apyDeltaPct: string;
	netBenefitUsdcRaw: string;
}

/* --- store ----------------------------------------------------------- */

interface CartState {
	intents: CartIntent[];
	add: (intent: NewIntent) => void;
	remove: (id: string) => void;
	clear: () => void;
	/** All intents merged into a single ops list (assetId summed). */
	ops: () => WireOp[];

	simulationData: TransactionsBundleSimulateResponse | null;
	simulationPending: boolean;
	simulationError: string | null;
	simulate: () => Promise<void>;

	executionData: TransactionsBundleExecuteResponse | null;
	executionPending: boolean;
	executionError: string | null;
	execute: () => Promise<void>;
}

type NewIntent =
	| Omit<WithdrawIntent, "id" | "createdAt">
	| Omit<RebalanceIntent, "id" | "createdAt">;

export const useCartStore = create<CartState>((set, get) => ({
	intents: [],
	simulationData: null,
	simulationPending: false,
	simulationError: null,
	executionData: null,
	executionPending: false,
	executionError: null,

	add(intent) {
		set((s) => {
			// Replace any existing rebalance intent — a rebalance plan
			// supersedes a stale one. Withdrawals stack.
			const withoutDup =
				intent.kind === "rebalance"
					? s.intents.filter((i) => i.kind !== "rebalance")
					: s.intents;
			return {
				intents: [
					...withoutDup,
					{
						...(intent as CartIntent),
						id: crypto.randomUUID(),
						createdAt: Date.now(),
					},
				],
				// Stale result on any intent change.
				simulationData: null,
				simulationError: null,
				executionData: null,
				executionError: null,
			};
		});
	},

	remove(id) {
		set((s) => ({
			intents: s.intents.filter((i) => i.id !== id),
			simulationData: null,
			simulationError: null,
			executionData: null,
			executionError: null,
		}));
	},

	clear() {
		set({
			intents: [],
			simulationData: null,
			simulationError: null,
			executionData: null,
			executionError: null,
		});
	},

	ops() {
		return mergeOps(get().intents);
	},

	async simulate() {
		const ops = mergeOps(get().intents);
		if (ops.length === 0) {
			set({
				simulationError: "Cart is empty.",
				simulationData: null,
			});
			return;
		}
		set({ simulationPending: true, simulationError: null });
		try {
			const data = await postTransactionsBundleSimulate(ops);
			set({ simulationData: data, simulationPending: false });
		} catch (err) {
			set({
				simulationError: (err as Error).message,
				simulationPending: false,
			});
		}
	},

	async execute() {
		const ops = mergeOps(get().intents);
		if (ops.length === 0) {
			set({ executionError: "Cart is empty.", executionData: null });
			return;
		}
		set({ executionPending: true, executionError: null });
		try {
			const data = await postTransactionsBundleExecute(ops);
			set({
				executionData: data,
				simulationData: simResponseFromExecute(data),
				executionPending: false,
			});
		} catch (err) {
			set({
				executionError: (err as Error).message,
				executionPending: false,
			});
		}
	},
}));

/**
 * Merge every intent into a single op list, summing deltas per assetId
 * so a stack of withdrawals + a rebalance net out cleanly. The planner
 * handles further netting at the token level.
 */
function mergeOps(intents: CartIntent[]): WireOp[] {
	const byAsset = new Map<string, bigint>();
	for (const intent of intents) {
		if (intent.kind === "withdraw") {
			const negative = -BigInt(intent.valueUsdcRaw);
			addDelta(byAsset, intent.assetId, negative);
		} else {
			for (const [assetId, deltaRaw] of Object.entries(intent.deltas)) {
				addDelta(byAsset, assetId, BigInt(deltaRaw));
			}
		}
	}
	const out: WireOp[] = [];
	for (const [assetId, delta] of byAsset) {
		if (delta === 0n) continue;
		out.push({ assetId, deltaValueRaw: delta.toString() });
	}
	return out;
}

function addDelta(map: Map<string, bigint>, assetId: string, d: bigint): void {
	map.set(assetId, (map.get(assetId) ?? 0n) + d);
}

function simResponseFromExecute(
	exec: TransactionsBundleExecuteResponse,
): TransactionsBundleSimulateResponse {
	return {
		wallet: exec.wallet,
		asOf: exec.asOf,
		input: exec.input,
		simulation: exec.simulation,
	};
}
